import { Badge } from "@/components/ui/badge";
import { InfoBanner } from "@/components/ui/banner";
import { IdentifierValue } from "@/components/ui/identifier-value";
import { WalletActivityObservationTime } from "@/features/wallets/wallet-activity-observation-time";
import { cn } from "@/lib/utils";
import type { WalletActivity, WalletActivityEntry } from "@/types";

/**
 * ALL-5458: the incoming-funds list of the wallet detail page.
 *
 * A transfer shows up here as soon as Chain Monitor sees it on chain, "Incoming · pending"
 * until it has enough confirmations and "Confirmed" after. The status is always a text label
 * (never colour alone). Nothing here invents data: an unreachable feed says so instead of
 * rendering an empty list that would read as "nothing has arrived".
 */

const STATUS_PRESENTATION: Record<
  WalletActivityEntry["status"],
  { label: string; variant: "pending" | "success" | "danger" }
> = {
  PENDING: { label: "Incoming · pending", variant: "pending" },
  CONFIRMED: { label: "Confirmed", variant: "success" },
  REORGED: { label: "Reorged · not received", variant: "danger" },
};

/** Today's transfers show the time; older ones add the date. The full timestamp is always in the title. */
function formatObservedAt(value: string): { short: string; full: string } {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return { short: "—", full: "" };
  const sameDay = parsed.toDateString() === new Date().toDateString();
  return {
    short: sameDay
      ? parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
      : parsed.toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }),
    full: parsed.toLocaleString(),
  };
}

export default function WalletActivityPanel({
  activity,
  loading,
}: {
  activity: WalletActivity | null;
  loading: boolean;
}) {
  const entries = activity?.activity ?? [];
  const monitoring = activity?.monitoring ?? "WATCHING";

  return (
    <section
      className="mb-4 overflow-hidden rounded-lg border border-line bg-surface"
      aria-labelledby="wallet-activity-title"
      data-testid="wallet-activity"
    >
      <div className="flex items-baseline justify-between border-b border-line px-5 pb-3.5 pt-[18px]">
        <h2 id="wallet-activity-title" className="text-[15px] font-bold text-ink">
          Incoming activity
        </h2>
        {monitoring === "WATCHING" && (
          <span className="text-xs text-muted" data-testid="wallet-activity-live">
            Watching this address · updates automatically
          </span>
        )}
      </div>

      {monitoring === "UNAVAILABLE" && (
        <div role="alert" data-testid="wallet-activity-unavailable" className="px-5 pt-4">
          <InfoBanner variant="warning" className="mb-0" title="Incoming activity is temporarily unavailable">
            We couldn&apos;t reach the chain monitor, so this list may be missing recent transfers. We&apos;ll retry
            automatically.
          </InfoBanner>
        </div>
      )}
      {monitoring === "NOT_CONFIGURED" && (
        <div data-testid="wallet-activity-not-configured" className="px-5 pt-4">
          <InfoBanner variant="info" className="mb-0">
            Incoming-funds monitoring isn&apos;t enabled for this wallet&apos;s network in this environment.
          </InfoBanner>
        </div>
      )}

      {loading && !activity ? (
        <div className="flex items-center gap-2 px-5 py-6 text-[13px] text-muted" data-testid="wallet-activity-loading">
          <div className="spinner" aria-hidden />
          Loading incoming activity…
        </div>
      ) : entries.length === 0 ? (
        monitoring === "WATCHING" && (
          <div className="px-5 py-6 text-[13px] text-muted" data-testid="wallet-activity-empty">
            No incoming funds yet. Send test funds to this wallet&apos;s address and they will appear here within about
            30 seconds of the first confirmation.
          </div>
        )
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="wallet-activity-table">
            <thead>
              <tr className="border-b border-line bg-bg text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Amount</th>
                <th className="px-4 py-2.5">From</th>
                <th className="px-4 py-2.5">Transaction</th>
                <th className="px-4 py-2.5">Seen</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => {
                const status = STATUS_PRESENTATION[e.status];
                return (
                  <tr
                    key={e.id}
                    data-testid="wallet-activity-row"
                    data-status={e.status}
                    className={cn(i < entries.length - 1 && "border-b border-line")}
                  >
                    <td className="px-4 py-3">
                      <Badge variant={status.variant} dot={false} size="sm">
                        {status.label}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-[13px] font-semibold text-success">
                      <span className="whitespace-nowrap">
                        +{e.amount} {e.asset.symbol}
                      </span>
                      {e.asset.kind === "ERC20" && !e.assetVerified && e.status === "CONFIRMED" && (
                        <div className="text-[11px] font-normal text-muted">Token not on the approved list</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-ink">
                      <IdentifierValue value={e.from} />
                    </td>
                    <td className="px-4 py-3 text-[13px] text-ink">
                      <div className="flex items-center gap-2">
                        <IdentifierValue value={e.transactionHash} />
                        {e.explorerUrl && (
                          <a
                            href={e.explorerUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whitespace-nowrap font-semibold text-primary hover:underline"
                          >
                            Etherscan ↗
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-[13px] text-muted" title={formatObservedAt(e.observedAt).full}>
                      {formatObservedAt(e.observedAt).short}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <WalletActivityObservationTime asOf={activity?.asOf} observed={activity !== null} />
    </section>
  );
}
